import { Injectable } from '@angular/core';
import {
  CodecraftAnswer,
  CodecraftQuestion,
  CodecraftQuestionApiResult,
  CodecraftQuizInstance,
  CodecraftQuizInstanceApiResult,
  CodecraftResult,
  Queue,
} from '@codecraft-works/data-models';
import { BehaviorSubject, Observable } from 'rxjs';

export type QuizState = {
  component: 'completed' | 'in-progress' | 'not-started' | 'resuming' | 'error';
  quizId?: string;
  questionId?: string;
  error?: string;
};

export type QuizReviewState = {
  component: 'review-table' | 'review' | 'error';
  quizId?: string;
  questionId?: string;
  error?: string;
};

type ApiError = {
  error: string;
};

@Injectable({
  providedIn: 'root',
})
export class QuizService {
  private userUid!: string;
  private stateSubject: BehaviorSubject<QuizState>;
  private quizInstance: BehaviorSubject<CodecraftQuizInstance>;
  private reviewStateSubject: BehaviorSubject<QuizReviewState>;

  setQuizReviewState(state: QuizReviewState) {
    if (!this.reviewStateSubject) {
      this.reviewStateSubject = new BehaviorSubject<QuizReviewState>(state);
    }
    this.reviewStateSubject.next(state);
  }

  getQuizReviewState(): QuizReviewState {
    return this.reviewStateSubject.getValue();
  }

  getQuizReviewState$(): Observable<QuizReviewState> {
    return this.reviewStateSubject.asObservable();
  }

  setQuizState(state: QuizState) {
    if (!this.stateSubject) {
      this.stateSubject = new BehaviorSubject<QuizState>(state);
    }
    this.stateSubject.next(state);
  }
  getQuizState(): QuizState {
    return this.stateSubject.getValue();
  }
  getQuizState$(): Observable<QuizState> {
    return this.stateSubject.asObservable();
  }

  private setQuizInstance(quizInstance: CodecraftQuizInstance) {
    if (!this.quizInstance) {
      this.quizInstance = new BehaviorSubject<CodecraftQuizInstance>(
        quizInstance
      );
    }
    this.quizInstance.next(quizInstance);
  }
  getQuizInstance$(): Observable<CodecraftQuizInstance> {
    return this.quizInstance.asObservable();
  }
  getQuizInstanceValue(): CodecraftQuizInstance {
    return this.quizInstance.getValue();
  }

  async initialize(uid: string, quizId: string): Promise<void> {
    this.userUid = uid;

    // no quizId or uid - error
    if (!quizId || quizId === '' || !uid || uid === '') {
      this.stateSubject = new BehaviorSubject<QuizState>({
        component: 'error',
        error: 'Invalid quiz state - missing quizId or uid',
      });
      return;
    }
    const isComplete = await this.isComplete(quizId);

    if (isComplete) {
      // all instances are complete - show completed page
      this.stateSubject = new BehaviorSubject<QuizState>({
        component: 'completed',
        quizId: quizId,
      });
      return;
    }

    // get current/new quizInstance
    const instance = await this.getQuizInstanceFirestore(quizId);
    this.quizInstance = new BehaviorSubject<CodecraftQuizInstance>(instance);

    let quizStarted = false;

    if (
      !instance ||
      !instance.questions ||
      !(instance.questions instanceof Map)
    ) {
      this.stateSubject = new BehaviorSubject<QuizState>({
        component: 'error',
        error: 'Invalid quiz state - missing quizInstance or questions',
      });
      return;
    }

    for (const question of instance.questions.values()) {
      if (question.resultId) {
        quizStarted = true;
        break;
      }
    }

    if (!quizStarted) {
      this.stateSubject = new BehaviorSubject<QuizState>({
        component: 'not-started',
        quizId: quizId,
      });
      return;
    }

    // found quizInstance that is in progress
    this.stateSubject = new BehaviorSubject<QuizState>({
      component: 'resuming',
      quizId: quizId,
    });
    return;
  }

  async initializeReviewTable(uid: string) {
    this.userUid = uid;
    try {
      this.setQuizReviewState({ component: 'review-table' });
    } catch (error) {
      this.setQuizReviewState({
        component: 'error',
        error: 'Failed to load quiz review table data',
      });
    }
  }

  async initializeQuizReview(quizInstance: CodecraftQuizInstance) {
    try {
      this.setQuizInstance(quizInstance);
      this.setQuizReviewState({
        component: 'review',
        quizId: quizInstance.quizId,
        questionId: Object.keys(quizInstance.questions)[0],
      });
    } catch (error) {
      this.setQuizReviewState({
        component: 'error',
        error: 'Failed to load quiz review data',
      });
    }
  }

  async restartQuiz() {
    const quizInstance = await this.getQuizInstanceFirestore();
    this.setQuizInstance(quizInstance);
    const quizState = this.getQuizState();
    this.setQuizState({
      component: 'not-started',
      quizId: quizState.quizId,
    });
  }

  resumeQuiz(quizInstance: CodecraftQuizInstance) {
    try {
      this.setQuizInstance(quizInstance);

      let firstQuestionId: string | null = null;

      if (quizInstance.queue) {
        if (quizInstance.queue instanceof Queue) {
          const firstQuestion = quizInstance.queue.peek();
          firstQuestionId = firstQuestion?.id || null;
        } else if (Array.isArray(quizInstance.queue)) {
          firstQuestionId = quizInstance.queue[0] || null;
        }
      }

      this.setQuizReviewState({
        component: null,
      });

      this.setQuizState({
        component: 'in-progress',
        quizId: quizInstance.quizId,
        questionId: firstQuestionId,
      });
    } catch (error) {
      console.error('❌ Error resuming quiz:', error);
      this.setQuizState({
        component: 'error',
        error: 'Failed to resume quiz',
      });
    }
  }

  async isComplete(quizId?: string): Promise<boolean> {
    if (!quizId) {
      const quizState = this.getQuizState();
      quizId = quizState.quizId;
    }

    const url = new URL('/api/quiz/isComplete', window.location.origin);
    url.search = new URLSearchParams({
      uid: this.userUid,
      quizId: quizId,
    }).toString();
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });

    const result = (await res.json()) as
      | {
          isComplete: boolean;
        }
      | ApiError;
    if ('error' in result) {
      return false;
    }
    return result.isComplete;
  }

  async submitAnswer(options: {
    question: CodecraftQuestion;
    answerGiven: CodecraftAnswer;
  }): Promise<
    | { error: true }
    | {
        error: false;
        correctAnswer: CodecraftAnswer;
        isCorrect: boolean;
      }
  > {
    const quizInstance = this.getQuizInstanceValue();
    const docOptions: {
      uid: string;
      quizId: string;
      instanceId: string;
      question: CodecraftQuestion;
      answerGiven: CodecraftAnswer;
    } = {
      ...options,
      question: this.convertFromCodecraftQuestion(options.question),
      answerGiven:
        options.answerGiven instanceof Map
          ? Object.fromEntries(options.answerGiven)
          : options.answerGiven,
      uid: this.userUid,
      instanceId: quizInstance.id,
      quizId: quizInstance.quizId,
    };

    const res = await fetch('/api/quiz/submitAnswer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ options: { ...docOptions } }),
      mode: 'same-origin',
    });

    const docResult = (await res.json()) as
      | {
          correctAnswer: CodecraftAnswer;
          isCorrect: boolean;
        }
      | ApiError;

    if ('error' in docResult) {
      return { error: true };
    }

    return { ...docResult, error: false };
  }

  nextQuestion() {
    const quizInstance = this.getQuizInstanceValue();
    // dequeue question
    if (!(quizInstance.queue instanceof Queue))
      throw new Error('Invalid queue type');
    quizInstance.queue.dequeue();
    this.setQuizInstance(quizInstance);

    const quizState = this.getQuizState();
    // mark quiz complete if no more questions
    if (quizInstance.queue.size() === 0) {
      this.setQuizState({
        component: 'completed',
        quizId: quizState.quizId,
      });
      return;
    }

    // set next question
    const queue = quizInstance.queue as Queue<CodecraftQuestion>;
    this.setQuizState({
      ...quizState,
      questionId: queue.peek().id,
    });
  }

  nextReviewQuestion(): boolean {
    const reviewState = this.getQuizReviewState();
    const quizInstance = this.getQuizInstanceValue();

    const questionIds = Object.keys(quizInstance.questions);
    const currentIndex = questionIds.indexOf(reviewState.questionId);

    if (currentIndex < questionIds.length - 1) {
      this.setQuizReviewState({
        ...reviewState,
        questionId: questionIds[currentIndex + 1],
      });
      return true; // Successfully moved to next
    }
    return false; // No more questions
  }

  previousReviewQuestion(): boolean {
    const reviewState = this.getQuizReviewState();
    const quizInstance = this.getQuizInstanceValue();

    const questionIds = Object.keys(quizInstance.questions);
    const currentIndex = questionIds.indexOf(reviewState.questionId);

    if (currentIndex > 0) {
      this.setQuizReviewState({
        ...reviewState,
        questionId: questionIds[currentIndex - 1],
      });
      return true; // Successfully moved to previous
    }
    return false; // Already at first question
  }

  async getAllQuizResults(uid?: string): Promise<CodecraftQuizInstance[]> {
    const url = new URL('/api/quiz/getAllQuizResults', window.location.origin);
    url.search = new URLSearchParams({
      uid,
    }).toString();

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });

    const docAllQuizResults = (await res.json()) as CodecraftQuizInstance[];

    if ('error' in docAllQuizResults) {
      throw new Error(
        'Get quiz completion details error: ' + docAllQuizResults.error
      );
    }
    return docAllQuizResults;
  }

  async getQuestionResult(resultId: string): Promise<CodecraftResult> {
    const url = new URL('/api/quiz/getQuestionResult', window.location.origin);
    url.search = new URLSearchParams({
      resultId: resultId,
    }).toString();
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });
    const docResult = (await res.json()) as CodecraftResult;
    return docResult;
  }

  async getQuestion(questionId: string): Promise<CodecraftQuestion> {
    const url = new URL('/api/quiz/getQuestion', window.location.origin);
    url.search = new URLSearchParams({
      questionId: questionId,
    }).toString();
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });
    const docResult = (await res.json()) as CodecraftQuestion;
    return docResult;
  }

  async getCompletionDetails(quizId?: string) {
    if (!quizId) {
      const quizState = this.getQuizState();
      quizId = quizState.quizId;
    }

    const url = new URL(
      '/api/quiz/getCompletionDetails',
      window.location.origin
    );
    url.search = new URLSearchParams({
      uid: this.userUid,
      quizId: quizId,
    }).toString();

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });

    const docCompletionDetails = (await res.json()) as
      | {
          completionDetails: {
            recentQuizInstance: CodecraftQuizInstance;
            bestQuizInstance: CodecraftQuizInstance;
          };
        }
      | ApiError;
    if ('error' in docCompletionDetails) {
      throw new Error(
        'Get quiz completion details error: ' + docCompletionDetails.error
      );
    }
    return docCompletionDetails.completionDetails;
  }

  async getQuizInstanceFirestore(
    quizId?: string
  ): Promise<CodecraftQuizInstance> {
    if (!quizId) {
      const quizState = this.getQuizState();
      quizId = quizState.quizId;
    }

    const url = new URL('/api/quiz/getQuizInstance', window.location.origin);
    url.search = new URLSearchParams({
      uid: this.userUid,
      quizId: quizId,
    }).toString();

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'same-origin',
    });

    const docQuizInstance = (await res.json()) as {
      quizInstance: CodecraftQuizInstanceApiResult;
    } | null;
    const quizInstance = this.fromFirestore(docQuizInstance.quizInstance);
    return quizInstance;
  }

  convertToCodecraftQuestion(
    question: CodecraftQuestionApiResult
  ): CodecraftQuestion {
    if (question.type === 'boolean') {
      return { ...question, answer: null };
    } else if (question.type === 'multiple') {
      return {
        ...question,
        answer: null,
        choices: new Map(Object.entries(question.choices)),
      };
    } else {
      return {
        ...question,
        answer: null,
        choices: new Map(Object.entries(question.choices)),
      };
    }
  }
  convertFromCodecraftQuestion(question: CodecraftQuestion): CodecraftQuestion {
    const choices =
      question.choices instanceof Map
        ? Object.fromEntries(question.choices)
        : question.choices;
    if (question.type === 'boolean' || question.type === 'single') {
      return {
        ...question,
        choices: choices,
      };
    } else if (question.type === 'multiple') {
      return {
        ...question,
        answer: null,
        choices: choices,
      };
    }
  }

  fromFirestore(
    quizInstance: CodecraftQuizInstanceApiResult
  ): CodecraftQuizInstance {
    const questionMap = new Map(
      Object.entries(quizInstance.questions).map(([questionId, question]) => {
        return [questionId, this.convertToCodecraftQuestion(question)];
      })
    );
    const queueArray: CodecraftQuestion[] = quizInstance.queue.map(
      (question) => {
        return this.convertToCodecraftQuestion(question);
      }
    );
    const queue = new Queue<CodecraftQuestion>(
      queueArray.length,
      false,
      queueArray
    );
    return {
      ...quizInstance,
      questions: questionMap,
      queue: queue,
    };
  }
}
